import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, useParams} from 'react-router';
import {useSnackbar} from 'notistack';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {Add, ArrowBack, Delete, Save} from '@mui/icons-material';
import Editor from '@monaco-editor/react';
import {useApplicationContext} from 'ApplicationContext';
import {useDocumentTitle} from 'Hooks/useDocumentTitle';
import {
    CreateProductExampleModel,
    ExampleSnippetDto,
    GetFileTypeData,
    GetProductExampleData,
    ProductExampleStatus,
    SnippetFormat,
    UpdateProductExampleModel,
} from 'apiClient/Api';

interface SnippetDraft {
    /** Server-assigned id for known snippets, or `local-N` for unsaved ones. */
    localKey: string;
    serverId: string | null | undefined;
    title: string;
    description: string;
    format: SnippetFormat;
    content: string;
}

interface FormState {
    fileTypeId: string;
    title: string;
    description: string;
    features: string[];
    targetVersion: string;
    status: ProductExampleStatus;
    notes: string;
    snippets: SnippetDraft[];
}

const emptyForm: FormState = {
    fileTypeId: '',
    title: '',
    description: '',
    features: [],
    targetVersion: '',
    status: ProductExampleStatus.Draft,
    notes: '',
    snippets: [],
};

const newLocalKey = (() => {
    let counter = 0;
    return () => `local-${++counter}`;
})();

const toForm = (data: GetProductExampleData): FormState => ({
    fileTypeId: data.fileTypeId ?? '',
    title: data.title ?? '',
    description: data.description ?? '',
    features: data.features ?? [],
    targetVersion: data.targetVersion ?? '',
    status: data.status ?? ProductExampleStatus.Draft,
    notes: data.notes ?? '',
    snippets: (data.snippets ?? []).map(s => ({
        localKey: s.id ?? newLocalKey(),
        serverId: s.id ?? null,
        title: s.title,
        description: s.description,
        format: s.format ?? SnippetFormat.Xml,
        content: s.content,
    })),
});

const toApiSnippets = (snippets: SnippetDraft[]): ExampleSnippetDto[] =>
    snippets.map(s => ({
        id: s.serverId ?? null,
        title: s.title,
        description: s.description,
        format: s.format,
        content: s.content,
    }));

/** Maps the snippet format enum to the Monaco language id. */
const monacoLanguage = (format: SnippetFormat): string => {
    switch (format) {
        case SnippetFormat.Json:
            return 'json';
        case SnippetFormat.Yaml:
            return 'yaml';
        case SnippetFormat.Ini:
            return 'ini';
        case SnippetFormat.PlainText:
            return 'plaintext';
        case SnippetFormat.Xml:
        default:
            return 'xml';
    }
};

/**
 * Editor for a single product example with multiple Monaco-edited snippets.
 *
 * Routes:
 *   /admin/product-examples/new   — create (no id)
 *   /admin/product-examples/:id   — edit
 *
 * On save the server returns the persisted shape (with snippet ids); the form
 * is rehydrated so subsequent saves are stable PUTs that preserve snippet
 * identities (and therefore download links).
 */
const ProductExampleEdit: React.FC = () => {
    const {t} = useTranslation();
    useDocumentTitle('productExamples.edit.title');
    const context = useApplicationContext();
    const navigate = useNavigate();
    const {enqueueSnackbar} = useSnackbar();
    const {id} = useParams<{id?: string}>();
    const isNew = !id || id === 'new';

    const [form, setForm] = useState<FormState>(emptyForm);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [products, setProducts] = useState<GetFileTypeData[]>([]);
    const [productsLoading, setProductsLoading] = useState(false);

    const loadProducts = useCallback(async () => {
        setProductsLoading(true);
        try {
            const response = await context.api.fileType.fileTypeGetFileTypes();
            setProducts(response.data ?? []);
        } catch {
            // picker stays empty; surface only via product field validation
        } finally {
            setProductsLoading(false);
        }
    }, [context.api.fileType]);

    const load = useCallback(async () => {
        if (isNew) {
            setForm(emptyForm);
            return;
        }
        setLoading(true);
        setLoadError(null);
        try {
            const response = await context.api.productExample.productExampleGet(id!);
            setForm(toForm(response.data));
        } catch {
            setLoadError(t('productExamples.edit.loadError'));
        } finally {
            setLoading(false);
        }
    }, [context.api.productExample, id, isNew, t]);

    useEffect(() => { loadProducts(); }, [loadProducts]);
    useEffect(() => { load(); }, [load]);

    const updateField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
        setForm(prev => ({...prev, [key]: value}));
    };

    const updateSnippet = (localKey: string, patch: Partial<SnippetDraft>) => {
        setForm(prev => ({
            ...prev,
            snippets: prev.snippets.map(s => s.localKey === localKey ? {...s, ...patch} : s),
        }));
    };

    const addSnippet = () => {
        setForm(prev => ({
            ...prev,
            snippets: [
                ...prev.snippets,
                {
                    localKey: newLocalKey(),
                    serverId: null,
                    title: '',
                    description: '',
                    format: SnippetFormat.Xml,
                    content: '',
                },
            ],
        }));
    };

    const removeSnippet = (localKey: string) => {
        setForm(prev => ({
            ...prev,
            snippets: prev.snippets.filter(s => s.localKey !== localKey),
        }));
    };

    const validate = (): string | null => {
        if (!form.fileTypeId) return t('productExamples.edit.validation.productRequired');
        if (!form.title.trim()) return t('productExamples.edit.validation.titleRequired');
        if (!form.description.trim()) return t('productExamples.edit.validation.descriptionRequired');
        for (const s of form.snippets) {
            if (!s.title.trim()) return t('productExamples.edit.validation.snippetTitleRequired');
            if (!s.description.trim()) return t('productExamples.edit.validation.snippetDescriptionRequired');
            if (!s.content.trim()) return t('productExamples.edit.validation.snippetContentRequired');
        }
        return null;
    };

    const save = async () => {
        const err = validate();
        if (err) {
            setValidationError(err);
            return;
        }
        setValidationError(null);
        setSaving(true);
        try {
            const payload: CreateProductExampleModel | UpdateProductExampleModel = {
                fileTypeId: form.fileTypeId,
                title: form.title.trim(),
                description: form.description.trim(),
                features: form.features,
                targetVersion: form.targetVersion.trim() || null,
                status: form.status,
                notes: form.notes.trim() || null,
                snippets: toApiSnippets(form.snippets),
            };
            const response = isNew
                ? await context.api.productExample.productExampleCreate(payload)
                : await context.api.productExample.productExampleUpdate(id!, payload);
            setForm(toForm(response.data));
            enqueueSnackbar(t('productExamples.edit.saved'), {variant: 'success'});
            if (isNew && response.data.id) {
                navigate(`/admin/product-examples/${response.data.id}`, {replace: true});
            }
        } catch (e: any) {
            const msg = e?.response?.data?.detail ?? e?.message ?? t('productExamples.edit.saveError');
            enqueueSnackbar(msg, {variant: 'error'});
        } finally {
            setSaving(false);
        }
    };

    const selectedProduct = products.find(p => p.id === form.fileTypeId) ?? null;

    return (
        <Box sx={{width: '100%', p: 2}}>
            <Stack
                direction={{xs: 'column', sm: 'row'}}
                spacing={2}
                sx={{alignItems: {xs: 'stretch', sm: 'center'}, justifyContent: 'space-between', mb: 3}}
            >
                <Stack direction="row" spacing={1} sx={{alignItems: 'center'}}>
                    <Tooltip title={t('productExamples.edit.back')}>
                        <IconButton onClick={() => navigate('/admin/product-examples')}>
                            <ArrowBack/>
                        </IconButton>
                    </Tooltip>
                    <Box>
                        <Typography variant="h4" component="h1">
                            {isNew ? t('productExamples.edit.newTitle') : t('productExamples.edit.title')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('productExamples.edit.subtitle')}
                        </Typography>
                    </Box>
                </Stack>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={saving ? <CircularProgress size={16}/> : <Save/>}
                    disabled={saving || loading}
                    onClick={save}
                >
                    {t('productExamples.edit.save')}
                </Button>
            </Stack>

            {loadError && <Alert severity="error" sx={{mb: 2}}>{loadError}</Alert>}
            {validationError && <Alert severity="warning" sx={{mb: 2}}>{validationError}</Alert>}

            {loading ? (
                <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
                    <CircularProgress/>
                </Box>
            ) : (
                <Stack spacing={3}>
                    {/* Metadata card */}
                    <Card variant="outlined">
                        <CardContent>
                            <Stack spacing={2}>
                                <Typography variant="h6">{t('productExamples.edit.metadata')}</Typography>

                                <Autocomplete<GetFileTypeData>
                                    options={products}
                                    loading={productsLoading}
                                    value={selectedProduct}
                                    onChange={(_, value) => updateField('fileTypeId', value?.id ?? '')}
                                    getOptionLabel={(option) => option.name ?? ''}
                                    isOptionEqualToValue={(a, b) => a.id === b.id}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={t('productExamples.fields.product')}
                                            required
                                            helperText={t('productExamples.fields.productHelper')}
                                        />
                                    )}
                                />

                                <TextField
                                    label={t('productExamples.fields.title')}
                                    value={form.title}
                                    onChange={(e) => updateField('title', e.target.value)}
                                    required
                                    fullWidth
                                    slotProps={{htmlInput: {maxLength: 200}}}
                                />
                                <TextField
                                    label={t('productExamples.fields.description')}
                                    value={form.description}
                                    onChange={(e) => updateField('description', e.target.value)}
                                    required
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    maxRows={6}
                                    helperText={t('productExamples.fields.descriptionHelper')}
                                />
                                <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                                    <Autocomplete
                                        multiple
                                        freeSolo
                                        options={[] as string[]}
                                        value={form.features}
                                        onChange={(_, value) => updateField('features', value as string[])}
                                        sx={{flex: 2}}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={t('productExamples.fields.features')}
                                                placeholder={t('productExamples.fields.featuresPlaceholder')}
                                                helperText={t('productExamples.fields.featuresHelper')}
                                            />
                                        )}
                                    />
                                    <TextField
                                        label={t('productExamples.fields.targetVersion')}
                                        value={form.targetVersion}
                                        onChange={(e) => updateField('targetVersion', e.target.value)}
                                        sx={{flex: 1}}
                                        slotProps={{htmlInput: {maxLength: 50}}}
                                    />
                                    <FormControl sx={{flex: 1, minWidth: 160}}>
                                        <InputLabel id="product-example-status">{t('productExamples.fields.status')}</InputLabel>
                                        <Select
                                            labelId="product-example-status"
                                            label={t('productExamples.fields.status')}
                                            value={form.status}
                                            onChange={(e) => updateField('status', e.target.value as ProductExampleStatus)}
                                        >
                                            <MenuItem value={ProductExampleStatus.Draft}>{t('productExamples.status.draft')}</MenuItem>
                                            <MenuItem value={ProductExampleStatus.Approved}>{t('productExamples.status.approved')}</MenuItem>
                                            <MenuItem value={ProductExampleStatus.Deprecated}>{t('productExamples.status.deprecated')}</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Stack>
                                <TextField
                                    label={t('productExamples.fields.notes')}
                                    value={form.notes}
                                    onChange={(e) => updateField('notes', e.target.value)}
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    maxRows={6}
                                    helperText={t('productExamples.fields.notesHelper')}
                                />
                            </Stack>
                        </CardContent>
                    </Card>

                    {/* Snippets */}
                    <Box>
                        <Stack direction="row" spacing={2} sx={{alignItems: 'center', justifyContent: 'space-between', mb: 2}}>
                            <Box>
                                <Typography variant="h6">
                                    {t('productExamples.edit.snippets')} ({form.snippets.length})
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t('productExamples.edit.snippetsHelper')}
                                </Typography>
                            </Box>
                            <Button startIcon={<Add/>} onClick={addSnippet}>
                                {t('productExamples.edit.addSnippet')}
                            </Button>
                        </Stack>

                        {form.snippets.length === 0 && (
                            <Paper variant="outlined" sx={{p: 4, textAlign: 'center'}}>
                                <Typography color="text.secondary" sx={{mb: 2}}>
                                    {t('productExamples.edit.noSnippets')}
                                </Typography>
                                <Button variant="outlined" startIcon={<Add/>} onClick={addSnippet}>
                                    {t('productExamples.edit.addFirstSnippet')}
                                </Button>
                            </Paper>
                        )}

                        <Stack spacing={2}>
                            {form.snippets.map((snippet, index) => (
                                <Card key={snippet.localKey} variant="outlined">
                                    <CardContent>
                                        <Stack spacing={2}>
                                            <Stack direction="row" spacing={1} sx={{alignItems: 'center'}}>
                                                <Typography variant="subtitle1" sx={{fontWeight: 600, flex: 1}}>
                                                    {t('productExamples.edit.snippetNumber', {index: index + 1})}
                                                </Typography>
                                                {snippet.serverId && (
                                                    <Chip
                                                        size="small"
                                                        variant="outlined"
                                                        label={`ID: ${snippet.serverId.slice(0, 8)}…`}
                                                        sx={{fontFamily: 'monospace'}}
                                                    />
                                                )}
                                                <Tooltip title={t('productExamples.edit.removeSnippet')}>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => removeSnippet(snippet.localKey)}
                                                    >
                                                        <Delete fontSize="small"/>
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>

                                            <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                                                <TextField
                                                    label={t('productExamples.fields.snippetTitle')}
                                                    value={snippet.title}
                                                    onChange={(e) => updateSnippet(snippet.localKey, {title: e.target.value})}
                                                    required
                                                    size="small"
                                                    sx={{flex: 2}}
                                                    slotProps={{htmlInput: {maxLength: 200}}}
                                                />
                                                <FormControl size="small" sx={{minWidth: 140}}>
                                                    <InputLabel id={`format-${snippet.localKey}`}>
                                                        {t('productExamples.fields.snippetFormat')}
                                                    </InputLabel>
                                                    <Select
                                                        labelId={`format-${snippet.localKey}`}
                                                        label={t('productExamples.fields.snippetFormat')}
                                                        value={snippet.format}
                                                        onChange={(e) => updateSnippet(snippet.localKey, {format: e.target.value as SnippetFormat})}
                                                    >
                                                        <MenuItem value={SnippetFormat.Xml}>XML</MenuItem>
                                                        <MenuItem value={SnippetFormat.Json}>JSON</MenuItem>
                                                        <MenuItem value={SnippetFormat.Yaml}>YAML</MenuItem>
                                                        <MenuItem value={SnippetFormat.Ini}>INI</MenuItem>
                                                        <MenuItem value={SnippetFormat.PlainText}>{t('productExamples.format.plainText')}</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Stack>

                                            <TextField
                                                label={t('productExamples.fields.snippetDescription')}
                                                value={snippet.description}
                                                onChange={(e) => updateSnippet(snippet.localKey, {description: e.target.value})}
                                                required
                                                fullWidth
                                                size="small"
                                                multiline
                                                minRows={2}
                                                maxRows={4}
                                            />

                                            <Divider/>

                                            <Box>
                                                <Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 0.5}}>
                                                    {t('productExamples.fields.snippetContent')}
                                                </Typography>
                                                <Box sx={{
                                                    border: 1,
                                                    borderColor: 'divider',
                                                    borderRadius: 1,
                                                    overflow: 'hidden',
                                                }}>
                                                    <Editor
                                                        height="320px"
                                                        language={monacoLanguage(snippet.format)}
                                                        value={snippet.content}
                                                        onChange={(value) => updateSnippet(snippet.localKey, {content: value ?? ''})}
                                                        options={{
                                                            minimap: {enabled: false},
                                                            scrollBeyondLastLine: false,
                                                            automaticLayout: true,
                                                            fontSize: 13,
                                                            wordWrap: 'on',
                                                            lineNumbers: 'on',
                                                            folding: true,
                                                            formatOnPaste: true,
                                                            tabSize: 2,
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            ))}
                        </Stack>
                    </Box>
                </Stack>
            )}
        </Box>
    );
};

export default ProductExampleEdit;
